/**
 * Raising a capacity cap from the promote dialog (pure).
 * For Media Devoted | Created By Ciro Autiero
 *
 * A paths cap is stored per SCOPE, not per routing group: one number for the
 * default bucket and ONE SHARED number for every rule group. So raising it for
 * a rule-group pool raises it for all of them — the dialog has to say so, which
 * is what `sharedWarning` is for.
 */

import type { CapacityRule } from '@trafficpulse/shared';

import {
  slotOfRule,
  updateRule,
} from '../../exclude/capacity/capacity-rules';

export type PromotePathScope = 'default' | 'rules';

const SLOT_OF: Record<PromotePathScope, string> = {
  default: 'paths-default',
  rules: 'paths-rules',
};

/** The capacity rule that owns this pool's cap, if one exists. */
export function ruleForScope(
  rules: CapacityRule[],
  scope: PromotePathScope,
): CapacityRule | undefined {
  return rules.find((r) => slotOfRule(r) === SLOT_OF[scope]);
}

/** The rule set with that scope's cap bumped by one. Null = nothing to raise. */
export function raiseCapFor(
  rules: CapacityRule[],
  scope: PromotePathScope,
  nextMax: number,
): CapacityRule[] | null {
  const rule = ruleForScope(rules, scope);
  if (!rule) return null;
  return updateRule(rules, { ...rule, max: nextMax });
}

/**
 * How many routing groups would silently inherit the higher cap. 0 for the
 * default bucket (precise) — anything else needs spelling out.
 */
export function sharedGroupCount(
  scope: PromotePathScope,
  poolIds: string[],
): number {
  if (scope !== 'rules') return 0;
  return poolIds.filter((id) => id.startsWith('flow:group:')).length;
}
